import { StatsigSingleton } from '@laam/statsig-client';
import { useToast } from '@laam/ui/base';
import type { MutateFunction } from '@tanstack/vue-query';
import { type PlaceOrderPayload } from '~/data/checkout';
import { useCheckoutStore } from '~/stores/checkout';
import { useCountryCode } from '~/stores/country';
import { useCurrency } from '~/stores/currency';
import { useGlobalStore } from '~/stores/global';
import { useLoganStore } from '~/stores/logan';
import type {
	PlaceOrderResponse,
	StripeRefType,
	XpayRefType,
} from '~/types/Checkout';
import {
	generatePurchaseEventData,
	generateThankyouObject,
	getProvinceName,
	getTrimmedPhoneNumber,
	getUsdTotal,
	updateAddressPayload,
	validateFinalPrice,
	validateShipping,
	validateTax,
	validateAddress,
} from '~/utils/checkout';
import countries from '~/utils/countries.json';
import { STRIPE, EASYPAISA } from './enums';

export const checkoutSubmit = async (
	xpayRef: (args: XpayRefType) => void,
	stripeRef: (args: StripeRefType) => void,
	placeOrder: MutateFunction<
		PlaceOrderResponse,
		{ status: number; error: Error },
		PlaceOrderPayload,
		unknown
	>,
	gaClientId: string,
	isPending: Ref<boolean>,
) => {
	// defining relevant variables and stores
	const { $eventClient, $cathodeClient } = useNuxtApp();
	const CheckoutStore = useCheckoutStore();
	const EasyPaisaAuthStore = useEasyPaisaStore();
	const LoganStore = useLoganStore();
	const CurrencyStore = useCurrency();
	const { currencyCode } = storeToRefs(CurrencyStore);
	const { $conversionRates, domain, storeType } = useGlobalStore();
	const { initiatePayment: initiateEasyPaisaPayment } = EasyPaisaAuthStore;
	const { toast } = useToast();

	const {
		values,
		usingValidatedAddress,
		formattedAddress,
		shipping,
		tax,
		zipCodeRef,
		grandTotal,
		subTotal,
		rewardsToBeRedeemed,
		couponCodeDiscount,
		checkoutHash,
		couponCodeDiscountInPkr,
		discountObject,
		shippingAddress,
		paymentMethod,
		billingAddress,
		countryCodeRef,
		contactInformation,
		deliveryMethodsExp,
		isFreeShipping,
		couponType,
		couponDiscountType,
		allowSaveAddress,
		billingAddressSame,
		binDiscount,
		binDiscountObject,
		productIds,
		eidGiftEnabled,
		giftingConfig,
		isDeliveryDaysExperiment,
		checkoutData,
		itemsCount,
	} = storeToRefs(CheckoutStore);

	const { openId } = storeToRefs(EasyPaisaAuthStore);
	const {
		convertRewardsToCoins,
		setPlaceOrderLoading,
		closeAddressValidationDialog,
		updateInputDisabled,
	} = CheckoutStore;
	const savyourCookie = useCookie('sav_aff_info');
	const isSavyourOrder = computed(() => !!savyourCookie.value);

	const { setLoading, updateAddressValidationDialog } = CheckoutStore;
	const ipCountryCode = useCountryCode().countryCode;
	const { path } = useRoute();
	// formatting phone number to required format
	const trimmedPhNumber = getTrimmedPhoneNumber(
		contactInformation.value?.phoneNumber!,
		countryCodeRef.value!,
	);

	const trimmedBillingPh = getTrimmedPhoneNumber(
		billingAddress.value?.phoneNumber!,
		billingAddress.value?.countryCode!,
	);
	// return if is pending
	if (isPending.value) return;

	setLoading(true);

	if (!shippingAddress.value) {
		throw new Error('shippingAddress.value is undefined');
	}

	// validating address for supported countries
	// usingValidatedAddress is set to true when we are using validated address
	if (usingValidatedAddress.value === false) {
		// Check if address is valid for countries with validation enabled
		const isAddressValid = await validateAddress(countryCodeRef.value!, {
			address1: shippingAddress.value.address1!,
			address2: shippingAddress.value.address2!,
			city: shippingAddress.value.city!,
			province: shippingAddress.value.province!,
			zip: shippingAddress.value.zip!,
		});

		// if not valid open dialog and return
		if (isAddressValid !== true) {
			updateAddressValidationDialog();
			setLoading(false);
			formattedAddress.value = isAddressValid;
			return;
		}
	}
	// if using validated address, close the dialog
	else {
		updateAddressValidationDialog();
	}

	const reqId = Date.now();
	// shipping and tax validation
	const shippingValidation = validateShipping(
		shipping.value!,
		isFreeShipping.value!,
	);

	$eventClient.sendEvent('validating-tax-shipping', {
		taxAmount: tax.value,
		shippingPrice: shipping.value!,
		countryCode: countryCodeRef.value!,
		province: shippingAddress.value?.province!,
		zipCode: zipCodeRef.value,
		stableId: StatsigSingleton.getStableID(),
		loganId: LoganStore.loganId,
		reqId: reqId,
	});
	const taxValidation = validateTax(
		tax.value,
		countryCodeRef.value!,
		shippingAddress.value?.province!,
	);
	// firing validation result events
	$eventClient.sendEvent('tax-shipping-validated', {
		isTaxValid: taxValidation,
		isShippingValid: shippingValidation,
		reqId: reqId,
		stableId: StatsigSingleton.getStableID(),
		loganId: LoganStore.loganId,
	});
	const totalValidation = validateFinalPrice(
		grandTotal.value,
		subTotal.value,
		tax.value,
		shipping.value!,
		values.value.usingRewards!,
		rewardsToBeRedeemed.value,
		couponCodeDiscount.value,
		binDiscount.value,
	);
	// if any validation fails, return
	if (!shippingValidation || !taxValidation || !totalValidation) {
		toast({
			title: 'Checkout validation failed, please refresh and try again.',
			duration: 3000,
		});
		setLoading(false);
		return;
	}
	// add phone prefix to phone number
	const phoneNumber =
		countries[
			countryCodeRef.value as keyof typeof countries
		].phone[0]?.toString()! + trimmedPhNumber;

	// fire place order click event now that we have done our validations
	$eventClient.sendEvent('checkout-place-order-click', {
		checkoutHash: checkoutHash.value,
		taxAmount: tax.value,
		taxRate: tax.value,
		logan: LoganStore.loganId,
		paymentMethod: paymentMethod.value!,
		subTotal: subTotal.value,
		shipping: shipping.value!,
		grandTotal: grandTotal.value,
		usdTotal: getUsdTotal(currencyCode.value, grandTotal.value),
		usingRewards: values.value.usingRewards!,
		rewardsToBeRedeemed: rewardsToBeRedeemed.value,
		country: ipCountryCode,
		exchangeRatePkr: $conversionRates![currencyCode.value]!.default_rate,
		exchangeRateUsd:
			$conversionRates![currencyCode.value]!.exchange_rate_to_usd,
		currency: currencyCode.value,
		couponType: couponType.value,
		discountType: couponDiscountType.value,
		couponDiscountInPkr: couponCodeDiscountInPkr.value,
		couponDiscountInNative: couponCodeDiscount.value,
	});

	$eventClient.login({
		email: contactInformation.value?.email!,
		firstName: shippingAddress.value?.firstName,
		lastName: shippingAddress.value?.lastName,
		phone: phoneNumber,
	});
	let provinceName = '';
	if (values.value.shippingAddress?.province) {
		const country =
			countries[values.value.countryCode as keyof typeof countries];
		const zones: {
			name: string;
			code: string;
		}[] = country && 'zones' in country ? country.zones : [];
		provinceName = getProvinceName(
			values.value.shippingAddress?.province!,
			zones,
		)?.name!;
	}
	// if country is canada check if there is space in zip after 3 characters if not add
	const zip =
		countryCodeRef.value === 'CA'
			? shippingAddress.value!.zip!.replace(/(\w{3})(\w{3})/, '$1 $2')
			: (shippingAddress.value!.zip as string);

	// make shipping address object
	const shippingAddressObj = {
		zip: zip,
		city:
			countryCodeRef.value === 'AE' && provinceName
				? provinceName
				: (shippingAddress.value!.city as string),
		phone: phoneNumber as string,
		country: countryCodeRef.value!,
		address1: shippingAddress.value!.address1 as string,
		address2: shippingAddress.value!.address2 as string,
		last_name: shippingAddress.value!.lastName as string,
		province: shippingAddress.value!.province as string,
		first_name: shippingAddress.value!.firstName as string,
	};

	// for creating billing address object
	let billingAddressObj;
	let billingAddressPhone;
	const useSameBillingAddress = billingAddressSame.value;

	if (useSameBillingAddress) {
		billingAddressPhone = (countries[
			countryCodeRef.value as keyof typeof countries
		].phone[0] + trimmedPhNumber!) as string;

		billingAddressObj = {
			...shippingAddressObj,
			phone: billingAddressPhone,
			country: countryCodeRef.value,
			city:
				countryCodeRef.value === 'AE' && provinceName
					? provinceName
					: (shippingAddress.value!.city as string),
		};
	} else {
		let billingProvinceName = '';
		if (values.value.billingAddress?.province) {
			const country =
				countries[
					values.value.billingAddress.countryCode as keyof typeof countries
				];
			const zones: {
				name: string;
				code: string;
			}[] = country && 'zones' in country ? country.zones : [];
			billingProvinceName = getProvinceName(
				values.value.billingAddress?.province!,
				zones,
			)?.name!;
		}
		billingAddressPhone =
			countries[billingAddress.value?.countryCode as keyof typeof countries]
				.phone[0] + trimmedBillingPh!;

		billingAddressObj = {
			phone: billingAddressPhone,
			zip: billingAddress.value!.zip as string,
			city:
				billingAddress.value?.countryCode === 'AE' && billingProvinceName
					? billingProvinceName
					: (billingAddress.value!.city as string),
			country: billingAddress.value?.countryCode,
			address1: billingAddress.value!.address1 as string,
			address2: billingAddress.value!.address2 as string,
			last_name: billingAddress.value!.lastName as string,
			province: billingAddress.value!.province as string,
			first_name: billingAddress.value!.firstName as string,
		};
	}

	const utm = sessionStorage.getItem('campaign') || '';
	const eidGift = [
		{
			name: 'eid_from',
			value: values.value.eidGift?.from ?? '',
		},
		{
			name: 'eid_to',
			value: values.value.eidGift?.to ?? '',
		},
		{
			name: 'eid_note',
			value: values.value.eidGift?.message ?? '',
		},
	];
	// note atrributes list
	const noteAttributes = [
		{
			name: 'conversion_rate_to_usd',
			value:
				$conversionRates[currencyCode.value]?.exchange_rate_to_usd?.toString(), //conversion rate from checkout currency to USD
		},
		{
			name: 'conversion_rate',
			value: $conversionRates[currencyCode.value]?.default_rate?.toString(), //conversion rate from PKR to checkout currency
		},
		{
			name: 'base_currency_code',
			value: currencyCode.value,
		},
		{
			name: 'utm',
			value: utm,
		},
		{
			name: 'gaClientId',
			value: gaClientId,
		},
		{
			name: 'UUID',
			value: useCookie('user_uuid').value ?? '',
		},
		{
			name: 'IndependentStore',
			value: domain,
		},
		{
			name: 'CheckoutHash',
			value: checkoutHash.value,
		},
		{
			name: 'StableId',
			value: StatsigSingleton.getStableID(),
		},
		...(deliveryMethodsExp.value
			? [
					{
						name: 'shipping_method',
						value: values.value.deliveryMethod,
					},
				]
			: []),
		...(isSavyourOrder.value
			? [
					{
						name: 'is_savyour',
						value: 'true',
					},
				]
			: []),

		...(couponCodeDiscount.value > 0
			? [
					{
						name: 'coupons',
						value: values.value.couponCode,
					},
					{
						name: 'couponamount',
						value:
							couponType.value === COUPON_TYPE.SHIPPING
								? '0'
								: couponCodeDiscountInPkr.value.toString(),
					},
					{
						name: 'logan_id',
						value: LoganStore.loganId.toString(),
					},
				]
			: []),
		...(binDiscount.value > 0
			? [
					{
						name: 'bin_discount_hash',
						value: binDiscountObject.value.discountHash,
					},
					{
						name: 'campaign_id',
						value: binDiscountObject.value.campainId.toString(),
					},
					{
						name: 'bin_number',
						value:
							paymentMethod.value === 'xPay'
								? binDiscountObject.value.binNumber
								: '',
					},
				]
			: []),
		...(isDeliveryDaysExperiment.value
			? [
					{
						name: 'delivery_days_experiment',
						value: true,
					},
				]
			: []),
		...(openId.value
			? [
					{
						name: 'open_id',
						value: openId.value,
					},
				]
			: []),
	];
	if (eidGiftEnabled.value && storeType === 'MARKETPLACE') {
		if (giftingConfig.value.allowEmpty) {
			noteAttributes.push(...eidGift);
		} else {
			if (
				values.value.eidGift?.from &&
				values.value.eidGift?.to &&
				values.value.eidGift?.message
			) {
				noteAttributes.push(...eidGift);
			}
		}
	}
	// creating place order payload which will be given to placeOrder mutation
	let obj: PlaceOrderPayload = {
		hash: checkoutHash.value,
		shipping_address: shippingAddressObj!,
		billing_address: billingAddressObj!,
		note_attributes: noteAttributes,
		base_curency_code: currencyCode.value,
		payment_type: paymentMethod.value!,
		is_hvc: LoganStore.isHvc,
		email: contactInformation.value?.email!,
	};
	// adding discount object to payload, which contains rewards, coupon, credit card discount if any
	obj = {
		...obj,
		...discountObject.value,
	};
	if (
		paymentMethod.value === PAYMENT_METHODS_NEW.COD ||
		paymentMethod.value === PAYMENT_METHODS_NEW.BANK_DEPOSIT
	) {
		setPlaceOrderLoading(true);
	} else {
		if (XPAY.includes(paymentMethod.value!)) {
			if (shippingAddressObj.zip == '') {
				shippingAddressObj.zip = '00000';
			}
		}
		updateInputDisabled(true);
	}

	try {
		// call place order mutation
		const data = await placeOrder(obj);

		sessionStorage.setItem('productIds', JSON.stringify(productIds.value));

		// if user is signed in and has no address saved, then save the entered address in address book
		if (allowSaveAddress.value) {
			const updatedAddress = updateAddressPayload(
				trimmedPhNumber!,
				shippingAddress.value?.firstName!,
				shippingAddress.value?.lastName!,
				shippingAddress.value?.address1!,
				shippingAddress.value?.address2,
				countryCodeRef.value === 'AE' && provinceName
					? provinceName
					: shippingAddress.value?.city!,
				shippingAddress.value?.zip,
				shippingAddress.value?.province,
				true,
				countryCodeRef.value!,
				undefined,
			);
			LoganStore.handleUserAddress(updatedAddress);
		}

		if (LoganStore.isSignedIn === false) {
			sessionStorage.setItem(
				'checkout-signin-data',
				JSON.stringify({
					firstName: shippingAddress.value?.firstName,
					lastName: shippingAddress.value?.lastName,
					email: contactInformation.value?.email,
				}),
			);
		}

		// if payment method is not card based
		// remove chash,  take user to thankyou page
		if (
			!XPAY.includes(paymentMethod.value!) &&
			paymentMethod.value !== STRIPE &&
			paymentMethod.value !== EASYPAISA
		) {
			// create checkout thank you object for cod or bank deposit payments

			const thankyouObj = generateThankyouObject(data, checkoutHash.value);
			sessionStorage.setItem('orderData', JSON.stringify(thankyouObj));
			localStorage.removeItem('cHash');
			const utmParams = convertQueryStringToObject(utm || '');
			const purchaseEventPayload = generatePurchaseEventData(
				grandTotal.value,
				'PKR',
				paymentMethod.value!,
				0, // this is for discount
				parseFloat(data.tax!)!,
				parseFloat(data.shipping!)!,
				data.order_name!,
				data.order!.toString(),
			);

			const purchaseCheckoutLocalizationPayload = {
				country: ipCountryCode,
				nativeTotalPrice: grandTotal.value,
				chargedTotalPrice:
					currencyCode.value !== 'pkr'
						? getUsdTotal(currencyCode.value, grandTotal.value)
						: grandTotal.value,
				chargedCurrency:
					currencyCode.value === 'pkr' && ipCountryCode === 'PK'
						? 'pkr'
						: 'usd',
				nativeToUsdRate:
					$conversionRates[currencyCode.value]?.exchange_rate_to_usd,
				nativeToPkrRate: $conversionRates[currencyCode.value]?.default_rate,
				rewardsAmountInNative: rewardsToBeRedeemed.value,
			};

			$eventClient.sendEvent(
				'purchase',
				{
					...purchaseEventPayload,
					...utmParams,
					lineItems: [],
					email: contactInformation.value!.email,
					phone: `${countries[countryCodeRef.value as keyof typeof countries].phone[0]!}${trimmedPhNumber}`,
					frontendTax: tax.value,
					frontendShipping: shipping.value,
					frontendSubtotal: subTotal.value,
					frontendGrandtotal: grandTotal.value,
					usingRewards: values.value.usingRewards,
					rewardsAmount: values.value.usingRewards
						? convertRewardsToCoins(rewardsToBeRedeemed.value).toString()
						: '0',
					purchaseCheckoutLocalizationPayload,
					couponDiscount: couponCodeDiscountInPkr.value,
					deliveryMethod: values.value.deliveryMethod,
					order_id: data.order!.toString(),
					items: checkoutData.value.map((item) => ({
						id: item.id,
						quantity: item.quantity,
					})),
					first_name: shippingAddress.value?.firstName,
					last_name: shippingAddress.value?.lastName,
					content_ids: checkoutData.value.map((item) => item.id),
					content_type: [],
					contents: checkoutData.value.map((item) => ({
						id: item.id,
						quantity: item.quantity,
					})),
					currency: 'PKR',
					value: grandTotal.value,
					num_items: itemsCount.value,
				},
				LoganStore.isHvc ? ['savyour'] : undefined,
			);

			$cathodeClient.queueEvent('purchase', purchaseEventPayload);

			setLoading(false);
			closeAddressValidationDialog();
			if (paymentMethod.value === PAYMENT_METHODS_NEW.COD) {
				const shopifyId = data.order;
				// take to cod verification if user is not signed in or is signed in but dont have email or phone number
				const noLoganRecord =
					LoganStore.isSignedIn &&
					(!LoganStore.email || !LoganStore.phoneNumber);
				if (
					(!LoganStore.isSignedIn || noLoganRecord) &&
					storeType == 'MARKETPLACE'
				) {
					// saving contact information in sessionStorage
					// will be used in verification page.
					sessionStorage.setItem(
						'checkout-verification-data',
						JSON.stringify({
							phone: trimmedPhNumber,
							orderNumber: data.order_name,
						}),
					);
					await navigateTo(`/checkout-verify?orderShopifyId=${shopifyId}`);
					return;
				} else if (storeType == 'OCTANE') {
					await navigateTo('/checkout-thankyou');
					return;
				}
			}
			if (storeType == 'MARKETPLACE') {
				await navigateTo('/checkout-thankyou');
			}
		}
		// if payment method is card
		else {
			// if xpay
			if (XPAY.includes(paymentMethod.value!)) {
				// Show EasyPaisa popup before payment processing begins
				if (paymentMethod.value === 'xpay-easypaisa') {
					CheckoutStore.toggleEasyPaisaPopup(true);
				}

				const xpayMetadata = {
					intentId: data.xpay_metadata?.intent_id!,
					piClientSecret: data.xpay_metadata?.pi_client_secret!,
					encryptionKey: data.xpay_metadata?.encryption_key!,
				};
				xpayRef({
					paymentType: data.payment_type!,
					xpayMetadata: xpayMetadata!,
					discounts: data.discounts!,
					potentialRewards: data.potential_rewards!,
				});
			}
			// if stripe
			else if (paymentMethod.value === STRIPE) {
				stripeRef({
					paymentType: data.payment_type!,
					stripeMetadata: {
						intentId: data.stripe_metadata?.intent_id!,
						clientSecret: data.stripe_metadata?.client_secret!,
					},
					discounts: data.discounts!,
					potentialRewards: data.potential_rewards!,
				});
			}
			// if easypaisa
			else if (paymentMethod.value === EASYPAISA) {
				if (data.intent_id) {
					initiateEasyPaisaPayment(data.intent_id);
				}
			}
		}
	} catch (error: any) {
		if (error?.error?.status === 415) {
			CheckoutStore.refetchGetCheckout();
		}

		closeAddressValidationDialog();
		$eventClient.sendEvent('checkout-errors', {
			path: path,
			errorTrace: 'Place order mutation',
			error: error.toString(),
			isApp: !!localStorage.getItem('isApp'),
			cookieCountry: ipCountryCode || 'PK',
			shippingCountry: countryCodeRef.value,
			jsonStringifiedError: JSON.stringify(error),
			subTotal: subTotal.value,
			grandTotal: grandTotal.value,
			shipping: shipping.value,
			tax: tax.value,
		});
		updateInputDisabled(false);
		usingValidatedAddress.value = false;
		toast({
			title: error.toString(),
		});
		setLoading(false);
	}
};
